import { checkClaimsWeb } from "@eventr/common";
import { createContext, useContext } from "react";
import { useShallow } from "zustand/react/shallow";
import { useBoundStore } from "./bound.store";
interface IRouteGuardContext {
  isAllowed: (guardCallbacks: CallbackTypes[]) => boolean;
}

const RouteGuardContext = createContext<IRouteGuardContext>({
  isAllowed: () => true,
});

export type CallbackTypes = "allowedInviteUsers" | "checkAdmin" | "allowedEdit" | "allowedCreateNewItin";

export const RouteGuardProvider = (props: { children: React.ReactNode }) => {
  const { orgConfig, claimsVersion, selectedItinID } = useBoundStore(
    useShallow((s) => {
      return {
        orgConfig: s.orgConfig,
        // checkClaimsWeb reads localStorage, which nothing re-renders on. The counter is
        // the subscription: a claims JWT arriving on a response header bumps it, and the
        // guards below are re-evaluated instead of holding the answer from first render.
        claimsVersion: s.claimsVersion,
        selectedItinID: s.selectedItinID,
      };
    }),
  );
  void claimsVersion;

  const callbackFunctions: { [key in CallbackTypes]: () => boolean } = {
    allowedInviteUsers: () => {
      return (
        !orgConfig.userInviteDisabled &&
        checkAllowEdit() &&
        checkClaimsWeb([`/`], ["createUser"]) &&
        selectedItinID !== -1
      );
    },
    allowedEdit: () => {
      return checkAllowEdit();
    },
    checkAdmin: () => {
      return checkAdmin();
    },
    allowedCreateNewItin: () => {
      return checkAllowedCreateNewItin();
    },
  };

  const isAllowed = (guardCallbacks: CallbackTypes[]) => {
    if (guardCallbacks.length === 0) return true;
    for (const callback of guardCallbacks) {
      if (!callbackFunctions[callback]()) return false;
    }
    return true;
  };

  const checkAllowEdit = () => {
    if (orgConfig?.editInAppDisabled === undefined) return false;
    if (orgConfig?.editInAppDisabled) return false;
    if (orgConfig?.adminEditOnly) {
      if (checkAdmin()) {
        return true;
      }
      return false;
    }

    return checkClaimsWeb([`/`], ["editItinerary"]);
  };

  const checkAllowedCreateNewItin = () => {
    if (orgConfig?.editInAppDisabled === undefined) return false;
    if (orgConfig?.editInAppDisabled) return false;
    if (orgConfig?.adminEditOnly) {
      if (checkAdmin()) {
        return true;
      }
      return false;
    }
    return checkClaimsWeb(["/"], ["createItinerary"]);
  };
  const checkAdmin = () => {
    return checkClaimsWeb(["/"], ["full"]);
  };

  return (
    <RouteGuardContext.Provider
      value={{
        isAllowed,
      }}
    >
      {props.children}
    </RouteGuardContext.Provider>
  );
};

export const useRouteGuardContext = () => {
  return useContext(RouteGuardContext);
};
